'use client';

import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { LeadershipManagementProps } from '@/types/leadership';
import { getOptimizedImageUrl } from '@/lib/optimized-strapi-image';

/** Split "Executing Excellence: The Management Team" → two display lines at the colon. */
function splitHeadingAtColon(heading: string): { firstLine: string; secondLine: string | null } {
  const trimmed = heading.trim();
  const colonIndex = trimmed.indexOf(':');
  if (colonIndex === -1) return { firstLine: trimmed, secondLine: null };

  const firstLine = trimmed.slice(0, colonIndex + 1).trim();
  const secondLine = trimmed.slice(colonIndex + 1).trim();
  return { firstLine, secondLine: secondLine || null };
}

export default function CoreTeamFeatured({ section }: LeadershipManagementProps) {
  const params = useParams();
  const locale = params.locale as string;
  const { firstLine, secondLine } = splitHeadingAtColon(section.heading);

  return (
    <section className="bg-white max-sm:px-4">
      <div className="container mx-auto">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-[#111111] sm:text-4xl md:text-5xl lg:text-[58px]">
            <span className="block">{firstLine}</span>
            {secondLine ? <span className="block">{secondLine}</span> : null}
          </h2>
          <div
            className="prose prose-gray mx-auto mt-3 max-w-2xl text-sm leading-6 text-[#555555] md:text-[15px] [&>p]:mb-0"
            dangerouslySetInnerHTML={{ __html: section.subHeading }}
          />
        </div>

        <div className="mt-10 grid gap-8 sm:mt-12 md:grid-cols-3 lg:mt-14 lg:grid-cols-4 lg:gap-4">
          {section.leadership_teams.map((member) => {
            const imageUrl = getOptimizedImageUrl(member.leaderImage, 'medium');
            const leaderSlug = member.leaderSlug.replace('/leadership/', '');
            const leaderDetailUrl = `/${locale}/leadership/${leaderSlug}`;

            return (
              <article key={member.id}>
                <Link href={leaderDetailUrl} className="block group">
                  <div className="relative overflow-hidden rounded-[10px] bg-[linear-gradient(180deg,#f1eadf_0%,#e3ddd2_100%)] transition-transform duration-300 group-hover:scale-[1.02]">
                    <div className="relative aspect-[1/1.02] w-full cursor-pointer sm:aspect-[0.95/1] lg:aspect-[0.88/0.9]">
                      <CmsImage
                        src={imageUrl}
                        alt={member.leaderName}
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                        sizes="(max-width: 768px) 100vw, (max-width: 1279px) 50vw, 25vw"
                        fallbackSrc={MEDIA_PLACEHOLDER}
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <h3 className="text-[20px] font-semibold tracking-[-0.02em] text-[#111111] transition-colors duration-200 group-hover:text-[#e34b42] md:text-[24px]">
                      {member.leaderName}
                    </h3>
                    <p className="mt-1 text-xs font-medium text-[#e34b42] md:text-[13px]">
                      {member.designation}
                    </p>
                    <p className="mt-3 text-sm leading-6 text-[#333333] md:mt-4 md:text-[14px]">
                      {member.excerpt}
                    </p>
                  </div>
                </Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
